import { useEffect, useRef, useState } from "react";
import { useInView } from "../../../hooks/useInView";

const stats = [
  { value: 3, suffix: "+", label: "Live Events", icon: "ri-calendar-event-fill" },
  { value: 5, suffix: "", label: "Active Wrestlers", icon: "ri-user-star-fill" },
  { value: 1, prefix: "#", label: "SoCal's Top Promotion", icon: "ri-trophy-fill" },
  { value: 2024, suffix: "", label: "Established", icon: "ri-shield-star-fill" },
];

function CountUp({ target, prefix = "", suffix = "", inView }: { target: number; prefix?: string; suffix?: string; inView: boolean }) {
  const [current, setCurrent] = useState(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const duration = 1200;

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(ease * target));
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [inView, target]);

  return <>{prefix}{current}{suffix}</>;
}

export default function WhyPWAStats() {
  const { ref, inView } = useInView(0.2);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="w-full bg-zinc-900 py-0 relative overflow-hidden"
    >
      {/* Dot grid background */}
      <div className="absolute inset-0 bg-dot-grid-dark pointer-events-none" />
      {/* Orange glow center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-32 bg-orange-500/8 blur-3xl rounded-full animate-glow-pulse" />
      </div>

      <div className="relative w-full grid grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={stat.label}
            className={`flex flex-col items-center justify-center py-10 px-6 gap-1 border-zinc-800 relative
              ${index < stats.length - 1 ? "border-r" : ""}
              ${index < 2 ? "border-b lg:border-b-0" : ""}
              group cursor-default transition-colors duration-300 hover:bg-orange-500/5
              ${inView ? "animate-count-up" : "anim-hidden"}
            `}
            style={{ animationDelay: `${index * 120}ms` }}
          >
            {/* Top accent line on hover */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />

            <div className="w-8 h-8 flex items-center justify-center mb-2">
              <i className={`${stat.icon} text-orange-500 text-xl transition-transform duration-300 group-hover:scale-125`} />
            </div>
            <span className="font-condensed font-black text-white text-4xl md:text-5xl leading-none tracking-tight">
              <CountUp target={stat.value} prefix={stat.prefix} suffix={stat.suffix} inView={inView} />
            </span>
            <span className="font-condensed font-semibold text-zinc-400 text-xs uppercase tracking-widest text-center mt-1">
              {stat.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
